import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Calendar, Mail, Clock, Sparkles, X, Check, ShieldCheck } from 'lucide-react';

const EventsPage = () => {
  const [showVerification, setShowVerification] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [verificationError, setVerificationError] = useState(false);

  // Obfuscated email parts (bots can't easily scrape this)
  const emailParts = ['luxeme', 'japan', '@', 'gmail', '.com'];
  const getEmail = () => emailParts[0] + emailParts[1] + emailParts[2] + emailParts[3] + emailParts[4];

  // Verification challenge - Japanese culture themed
  const challenge = {
    question: "Which Japanese flower blooms in spring and symbolises beauty?",
    options: [
      { id: 1, label: "Sakura",  image: "🌸", correct: true  },
      { id: 2, label: "Lotus",   image: "🪷", correct: false },
      { id: 3, label: "Orchid",  image: "🌺", correct: false },
    ]
  };

  const eventDetails = {
    name: "PRESS & B2B DAY 2026",
    subtitle: "LUXE ME JAPAN",
    tagline: "Unlock Possibilities",
    venue: "Hotel St. Regis",
    address: "6 Jalan Stesen Sentral 2, Kuala Lumpur, Wilayah Persekutuan KL, Malaysia",
    dateRange: "January 2026",
    heroImage: "https://images.unsplash.com/photo-1478720568477-152d9b164e26?q=80&w=2070&auto=format&fit=crop",
    days: [
      {
        id: 1,
        dayNumber: "DAY 1",
        date: "29th January 2026",
        title: "Press Day",
        audience: "Media & Influencers",
        image: "/images/event-day1.jpg"
      },
      {
        id: 2,
        dayNumber: "DAY 2",
        date: "30th January 2026",
        title: "B2B Day",
        audience: "Distributors & Retailers",
        image: "/images/event-day2.jpg"
      }
    ]
  };

  const handleRequestClick = () => {
    if (isVerified) {
      sendEmail();
    } else {
      setShowVerification(true);
      setSelectedAnswer(null);
      setVerificationError(false);
    }
  };

  const handleVerify = () => {
    if (!selectedAnswer) return;
    
    const selected = challenge.options.find(opt => opt.id === selectedAnswer);
    if (selected && selected.correct) {
      setIsVerified(true);
      setShowVerification(false);
      // Small delay then open email
      setTimeout(() => {
        sendEmail();
      }, 300);
    } else {
      setVerificationError(true);
      setSelectedAnswer(null);
    }
  };

  const sendEmail = () => {
    const email = getEmail();
    const subject = encodeURIComponent("Request for Event Participation - PRESS & B2B DAY 2026");
    const body = encodeURIComponent(`Dear Luxe Me Japan Team,

I am writing to request participation in the PRESS & B2B DAY 2026 event.

Name: 
Company/Organization: 
Role: [Media / Influencer / Distributor / Retailer]
Website/Social Media: 

I look forward to hearing from you.

Best regards`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-onyx">
      <Helmet>
        <title>Events - Luxe Me Japan | Press & B2B Day 2026 Kuala Lumpur</title>
        <meta name="description" content="Join our exclusive invitation-only event in Kuala Lumpur, January 2026. Connect with media, influencers, and B2B buyers at the St. Regis Hotel. RSVP now." />
      </Helmet>
      
      {/* Hero Section */}
      <header className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={eventDetails.heroImage}
            className="w-full h-full object-cover opacity-30 brightness-50"
            alt="Event Background"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-onyx/60 via-onyx/40 to-onyx"></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-1/4 left-10 w-32 h-32 border border-gold/20 rounded-full animate-pulse hidden lg:block"></div>
        <div className="absolute bottom-1/4 right-10 w-48 h-48 border border-gold/10 rounded-full hidden lg:block"></div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto">
          {/* Tagline */}
          <div className="inline-flex items-center gap-3 mb-6 sm:mb-8 opacity-0 animate-fade-up">
            <Sparkles className="w-5 h-5 text-gold" />
            <span className="text-gold tracking-[0.4em] text-xs sm:text-sm uppercase font-light">
              {eventDetails.tagline}
            </span>
            <Sparkles className="w-5 h-5 text-gold" />
          </div>

          {/* Main Title */}
          <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl lg:text-8xl mb-6 sm:mb-8 opacity-0 animate-fade-up stagger-1 leading-tight">
            <span className="text-gradient">PRESS & B2B</span>
            <br />
            <span className="text-platinum">DAY 2026</span>
          </h1>

          {/* Date & Location Banner */}
          <div className="opacity-0 animate-fade-up stagger-2">
            <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-platinum/80 text-sm sm:text-lg tracking-widest uppercase font-light">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gold" />
                {eventDetails.dateRange}
              </span>
              <span className="hidden sm:block text-gold">|</span>
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-gold" />
                {eventDetails.venue}, Kuala Lumpur
              </span>
            </div>
          </div>

          {/* CTA Button */}
          <div className="mt-10 sm:mt-12 opacity-0 animate-fade-up stagger-3">
            <a
              href="#event-details"
              className="inline-flex items-center gap-3 bg-gold text-onyx px-8 sm:px-12 py-4 sm:py-5 rounded-full font-medium tracking-widest uppercase text-xs hover:bg-white transition-all duration-500 shine-effect"
            >
              Get Ready & Be Inspired
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-40">
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex items-start justify-center pt-2">
            <div className="w-1 h-3 bg-gold rounded-full animate-pulse"></div>
          </div>
        </div>
      </header>

      {/* Event Details Section */}
      <section id="event-details" className="py-20 sm:py-32 px-4 sm:px-6 lg:px-12 bg-ebony">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 sm:mb-24">
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl mb-4">
              Event <span className="text-gold italic">Details</span>
            </h2>
            <div className="w-24 h-px bg-gold mx-auto"></div>
          </div>

          {/* Venue Card - Beautiful Frame */}
          <div className="max-w-4xl mx-auto mb-20 sm:mb-32">
            <div className="relative">
              {/* Decorative Border */}
              <div className="absolute -inset-3 sm:-inset-4 border border-gold/30 rounded-3xl"></div>
              <div className="absolute -inset-6 sm:-inset-8 border border-gold/10 rounded-3xl hidden sm:block"></div>
              
              {/* Main Card */}
              <div className="relative bg-gradient-to-br from-onyx via-ebony to-onyx p-8 sm:p-12 lg:p-16 rounded-2xl border border-gold/20">
                {/* Corner Decorations */}
                <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-gold/40 rounded-tl-lg"></div>
                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-gold/40 rounded-tr-lg"></div>
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-gold/40 rounded-bl-lg"></div>
                <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-gold/40 rounded-br-lg"></div>

                <div className="text-center space-y-6 sm:space-y-8">
                  {/* Event Name */}
                  <div>
                    <p className="text-gold text-xs sm:text-sm tracking-[0.3em] uppercase mb-2">Event Name</p>
                    <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-platinum">
                      {eventDetails.name} - {eventDetails.subtitle}
                    </h3>
                  </div>

                  {/* Divider */}
                  <div className="flex items-center justify-center gap-4">
                    <span className="w-16 sm:w-24 h-px bg-gold/30"></span>
                    <Sparkles className="w-4 h-4 text-gold" />
                    <span className="w-16 sm:w-24 h-px bg-gold/30"></span>
                  </div>

                  {/* Venue */}
                  <div>
                    <p className="text-gold text-xs sm:text-sm tracking-[0.3em] uppercase mb-2">Venue</p>
                    <h4 className="font-serif text-xl sm:text-2xl lg:text-3xl text-platinum mb-2">
                      {eventDetails.venue}
                    </h4>
                  </div>

                  {/* Address */}
                  <div>
                    <p className="text-gold text-xs sm:text-sm tracking-[0.3em] uppercase mb-2">Address</p>
                    <p className="text-platinum/70 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
                      {eventDetails.address}
                    </p>
                  </div>

                  {/* Date */}
                  <div className="pt-4">
                    <p className="text-gold text-xs sm:text-sm tracking-[0.3em] uppercase mb-3">Date</p>
                    <div className="inline-flex items-center gap-3 bg-gold/10 px-6 sm:px-8 py-3 sm:py-4 rounded-full border border-gold/30">
                      <Calendar className="w-5 h-5 text-gold" />
                      <span className="font-serif text-lg sm:text-xl text-gold">29-30 January 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Event Days Grid */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {eventDetails.days.map((day) => (
              <div key={day.id} className="group relative">
                {/* Card */}
                <div className="relative overflow-hidden rounded-2xl bg-onyx border border-white/5 hover:border-gold/30 transition-all duration-700">
                  {/* Image */}
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={day.image}
                      alt={day.title}
                      className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-onyx via-onyx/60 to-transparent"></div>
                    
                    {/* Invitation Only Badge */}
                    <div className="absolute top-6 right-6 z-20">
                      <div className="relative">
                        {/* Glow Effect */}
                        <div className="absolute inset-0 bg-gold/30 blur-xl rounded-full"></div>
                        {/* Badge */}
                        <div className="relative bg-gradient-to-r from-[#d4b068] to-gold px-5 py-2.5 rounded-full shadow-lg shadow-gold/20 border border-gold/50">
                          <span className="text-onyx text-[10px] sm:text-xs font-bold tracking-widest uppercase">
                            Invitation Only
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Day Number Badge */}
                    <div className="absolute top-6 left-6">
                      <div className="bg-onyx/80 backdrop-blur-sm px-4 py-2 rounded-full border border-gold/30">
                        <span className="text-gold text-xs font-bold tracking-widest">{day.dayNumber}</span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 sm:p-8 text-center">
                    {/* Date */}
                    <div className="flex items-center justify-center gap-2 text-gold mb-4">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm tracking-widest uppercase">{day.date}</span>
                    </div>

                    {/* Title */}
                    <h3 className="font-serif text-3xl sm:text-4xl mb-3 text-platinum group-hover:text-gold transition-colors">
                      {day.title}
                    </h3>

                    {/* Audience */}
                    <p className="text-platinum/60 text-lg">{day.audience}</p>

                    {/* Decorative Line */}
                    <div className="mt-6 h-px w-0 group-hover:w-full bg-gradient-to-r from-transparent via-gold to-transparent transition-all duration-1000 mx-auto"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Exclusive Invitation Section */}
      <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-12 bg-onyx relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Icon */}
          <div className="w-20 h-20 mx-auto mb-8 rounded-full border-2 border-gold/30 flex items-center justify-center bg-ebony">
            <Mail className="w-8 h-8 text-gold" />
          </div>

          {/* Title */}
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-6">
            Exclusive <span className="text-gold italic">Invitation</span>
          </h2>

          {/* Description */}
          <div className="space-y-6 mb-10 sm:mb-12">
            <p className="text-platinum/70 text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto">
              This exclusive event is open only to selected professionals in the beauty and lifestyle industry.
            </p>
            
            {/* Audience Categories */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <div className="bg-ebony px-6 py-3 rounded-full border border-gold/20">
                <span className="text-gold text-sm font-medium tracking-wide">Influencers</span>
              </div>
              <div className="bg-ebony px-6 py-3 rounded-full border border-gold/20">
                <span className="text-gold text-sm font-medium tracking-wide">Media</span>
              </div>
              <div className="bg-ebony px-6 py-3 rounded-full border border-gold/20">
                <span className="text-gold text-sm font-medium tracking-wide">B2B Buyers</span>
              </div>
            </div>

            <p className="text-platinum/50 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
              Participation is available only upon invitation. If you believe you qualify and would like to attend, 
              please reach out to us directly.
            </p>
          </div>

          {/* CTA Button with Shield Icon indicating protection */}
          <button
            onClick={handleRequestClick}
            className="group inline-flex items-center gap-3 bg-gold text-onyx px-10 sm:px-14 py-4 sm:py-5 rounded-full font-bold tracking-widest uppercase text-xs hover:bg-white transition-all duration-500 shine-effect"
          >
            {isVerified ? (
              <Check className="w-5 h-5" />
            ) : (
              <ShieldCheck className="w-5 h-5 group-hover:rotate-12 transition-transform" />
            )}
            Request Participation
          </button>

          {/* Protected Notice */}
          <p className="mt-6 text-platinum/40 text-sm flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Protected contact - verification required
          </p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-12 bg-ebony border-t border-gold/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-platinum/50 text-sm sm:text-base tracking-wide mb-4">
            We look forward to welcoming you at
          </p>
          <h3 className="font-serif text-2xl sm:text-3xl text-platinum mb-2">
            Hotel St. Regis, Kuala Lumpur
          </h3>
          <p className="text-gold tracking-widest text-sm uppercase">
            29-30 January 2026
          </p>
        </div>
      </section>

      {/* Verification Modal */}
      {showVerification && (
        <div 
          className="fixed inset-0 z-[100] bg-onyx/95 backdrop-blur-xl flex items-center justify-center p-4"
          onClick={() => setShowVerification(false)}
        >
          <div 
            className="bg-ebony rounded-3xl max-w-md w-full border border-gold/20 shadow-2xl shadow-gold/10 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-gold/10 to-transparent p-6 border-b border-gold/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-6 h-6 text-gold" />
                  <h3 className="font-serif text-xl text-platinum">Verification</h3>
                </div>
                <button 
                  onClick={() => setShowVerification(false)}
                  className="text-platinum/50 hover:text-gold transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 sm:p-8">
              <p className="text-platinum/70 text-center mb-6">
                Please verify you are human to access our contact.
              </p>

              {/* Question */}
              <p className="text-gold text-center font-medium mb-6">
                {challenge.question}
              </p>

              {/* Options */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {challenge.options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => {
                      setSelectedAnswer(option.id);
                      setVerificationError(false);
                    }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-300 ${
                      selectedAnswer === option.id
                        ? 'border-gold bg-gold/10 scale-105'
                        : 'border-white/10 hover:border-gold/50'
                    }`}
                  >
                    <span className="text-4xl">{option.image}</span>
                    <span className="text-xs text-platinum/70">{option.label}</span>
                  </button>
                ))}
              </div>

              {/* Error Message */}
              {verificationError && (
                <p className="text-red-400 text-sm text-center mb-4">
                  Incorrect answer. Please try again.
                </p>
              )}

              {/* Verify Button */}
              <button
                onClick={handleVerify}
                disabled={!selectedAnswer}
                className={`w-full py-4 rounded-full font-bold tracking-widest uppercase text-xs transition-all duration-500 ${
                  selectedAnswer
                    ? 'bg-gold text-onyx hover:bg-white'
                    : 'bg-platinum/20 text-platinum/40 cursor-not-allowed'
                }`}
              >
                Verify & Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
